import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { getApiBaseUrl } from '../utils/apiUtils';

interface User {
  email: string;
  name?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('api_token'));
  const [user, setUser] = useState<User | null>(null);

  const login = async (email: string, _password: string) => {
    try {
      const API_BASE_URL = getApiBaseUrl();
      // Send both email and name to cover APIs that expect 'name' instead of 'email'
      const res = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: email, password: _password })
      });

      if (!res.ok) {
        // Try to parse JSON error body when available to provide clearer message
        let errMsg = `Falha no login: ${res.status} ${res.statusText}`;
        try {
          const errBody = await res.json();
          if (errBody && errBody.message) errMsg += ` - ${errBody.message}`;
        } catch {
          const text = await res.text().catch(() => '');
          if (text) errMsg += ` - ${text}`;
        }
        throw new Error(errMsg);
      }

      const data = await res.json().catch(() => ({} as any));
      const token = data?.token || data?.access_token || data?.accessToken;
  if (!token) throw new Error('Token não recebido da API');

      // Salva token no localStorage para uso global
      localStorage.setItem('api_token', token);

      setIsAuthenticated(true);
      setUser({ email: data?.user?.email || email, name: data?.user?.name });
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      throw error;
    }
  };

  // Remove token and reset auth
  const logout = () => {
    localStorage.removeItem('api_token');
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
} 